import React, { useEffect, useState } from "react";
import {
  ALERT_RULE_TYPES,
  ALERT_SCHEDULES,
  validateAlertRule,
} from "./alertValidation";

const TEMPLATE_PLACEHOLDERS = [
  "{{ first_name }}",
  "{{ email }}",
  "{{ reason }}",
  "{{ rule_name }}",
  "{{ threshold_days }}",
  "{{ cta_url }}",
  "{{ cta_label }}",
  "{{ unsubscribe_url }}",
];

const SCHEDULE_OPTIONS = [
  {
    value: "daily",
    label: "Daily: Send once eligible, then at most once per day",
  },
  {
    value: "weekly",
    label: "Weekly: Send once eligible, then at most once every 7 days",
  },
];

const ALERT_PRESETS = [
  {
    key: "signup-no-progress",
    label: "Signup but no progress",
    values: {
      name: "Signup but no progress",
      rule_type: "no_section_start_after_signup",
      threshold_days: 2,
      schedule: "weekly",
      enabled: true,
      filters: "",
      email_subject: "Pick up where you left off",
      email_body_template:
        "Hi {{ first_name }},\n\nYou signed up but have not started yet. Continue here: {{ cta_url }}",
      email_html_template:
        "<p>Hi {{ first_name }},</p><p>You signed up but have not started yet.</p><p>Use the button below to continue.</p>",
      cta_label: "Continue",
      cta_url: "https://your-web-app-url/path",
      max_deliveries_per_user: 3,
    },
  },
];

const INITIAL_FORM = {
  name: "",
  rule_type: ALERT_RULE_TYPES[0],
  threshold_days: 1,
  schedule: ALERT_SCHEDULES[0],
  enabled: true,
  filters: "",
  email_subject: "",
  email_body_template: "",
  email_html_template: "",
  cta_label: "",
  cta_url: "",
  max_deliveries_per_user: "",
};

const AlertRuleForm = ({
  selectedRule,
  loading = false,
  onSubmit,
  onCancel,
}) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!selectedRule) {
      setForm(INITIAL_FORM);
      setErrors({});
      return;
    }

    setForm({
      ...INITIAL_FORM,
      ...selectedRule,
      threshold_days: Number(selectedRule.threshold_days || 1),
      enabled: selectedRule.enabled !== false,
      filters: selectedRule.filters
        ? JSON.stringify(selectedRule.filters, null, 2)
        : "",
      email_subject: selectedRule.email_subject || "",
      email_html_template: selectedRule.email_html_template || "",
      cta_label: selectedRule.cta_label || "",
      cta_url: selectedRule.cta_url || "",
      max_deliveries_per_user:
        selectedRule.max_deliveries_per_user ?? "",
    });
    setErrors({});
  }, [selectedRule]);

  const submit = (event) => {
    event.preventDefault();
    const nextErrors = validateAlertRule(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    let parsedFilters;
    if (String(form.filters || "").trim()) {
      try {
        parsedFilters = JSON.parse(form.filters);
      } catch {
        setErrors((prev) => ({ ...prev, filters: "Filters must be valid JSON." }));
        return;
      }
    }
    onSubmit({
      name: form.name,
      rule_type: form.rule_type,
      threshold_days: Number(form.threshold_days),
      schedule: form.schedule,
      enabled: Boolean(form.enabled),
      ...(parsedFilters ? { filters: parsedFilters } : {}),
      ...(form.email_subject ? { email_subject: form.email_subject } : {}),
      email_html_template: String(form.email_html_template || "").trim() || null,
      cta_label: String(form.cta_label || "").trim() || null,
      cta_url: String(form.cta_url || "").trim() || null,
      max_deliveries_per_user:
        String(form.max_deliveries_per_user).trim() === ""
          ? null
          : Number(form.max_deliveries_per_user),
      ...(form.email_body_template
        ? { email_body_template: form.email_body_template }
        : {}),
    });
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-5 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_14px_40px_rgba(15,23,42,0.06)]"
    >
      <div>
        <h3 className="text-2xl font-semibold text-slate-900">
        {selectedRule ? "Edit Alert Rule" : "Create Alert Rule"}
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          Schedule controls resend cadence after a user becomes eligible. First send happens as soon as the threshold is met.
        </p>
      </div>

      <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-slate-900">Quick preset</p>
            <p className="text-xs text-slate-600">
              Load a recommended example for a signup reminder with resend limits.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {ALERT_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className="rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
                onClick={() => {
                  setForm((prev) => ({
                    ...prev,
                    ...preset.values,
                  }));
                  setErrors({});
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field
          label="Name"
          value={form.name}
          onChange={(value) => setForm((prev) => ({ ...prev, name: value }))}
          error={errors.name}
        />

        <SelectField
          label="Rule Type"
          value={form.rule_type}
          onChange={(value) => setForm((prev) => ({ ...prev, rule_type: value }))}
          options={ALERT_RULE_TYPES}
          error={errors.rule_type}
        />

        <Field
          label="Threshold Days"
          value={form.threshold_days}
          type="number"
          min={1}
          helpText="How long a user must remain in the condition before the first alert can send."
          onChange={(value) =>
            setForm((prev) => ({ ...prev, threshold_days: Number(value) }))
          }
          error={errors.threshold_days}
        />

        <SelectField
          label="Schedule"
          value={form.schedule}
          onChange={(value) => setForm((prev) => ({ ...prev, schedule: value }))}
          options={SCHEDULE_OPTIONS}
          helpText="Resend cadence only. It does not delay the first send beyond the threshold."
          error={errors.schedule}
        />

        <Field
          label="Max Deliveries Per User"
          value={form.max_deliveries_per_user}
          type="number"
          min={1}
          onChange={(value) =>
            setForm((prev) => ({
              ...prev,
              max_deliveries_per_user: value,
            }))
          }
          error={errors.max_deliveries_per_user}
          helpText="Stops reminders after this many emails per user"
        />
      </div>

      <label className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, enabled: e.target.checked }))
          }
        />
        Enabled
      </label>

      <Field
        label="Email Subject"
        value={form.email_subject || ""}
        onChange={(value) =>
          setForm((prev) => ({ ...prev, email_subject: value }))
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field
          label="CTA Label"
          value={form.cta_label || ""}
          onChange={(value) =>
            setForm((prev) => ({ ...prev, cta_label: value }))
          }
        />
        <Field
          label="CTA URL"
          value={form.cta_url || ""}
          onChange={(value) =>
            setForm((prev) => ({ ...prev, cta_url: value }))
          }
          error={errors.cta_url}
        />
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-slate-500">Filters JSON (Optional Advanced)</span>
        <textarea
          value={form.filters || ""}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, filters: e.target.value }))
          }
          rows={4}
          placeholder='{"course_id":"..."}'
          className="rounded-xl border border-slate-200 px-3 py-2"
        />
        {errors.filters && <span className="text-xs text-red-600">{errors.filters}</span>}
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-slate-500">Email Body Template</span>
        <textarea
          value={form.email_body_template || ""}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, email_body_template: e.target.value }))
          }
          rows={6}
          className="rounded-xl border border-slate-200 px-3 py-2"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-slate-500">HTML Template (Optional)</span>
        <textarea
          value={form.email_html_template || ""}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, email_html_template: e.target.value }))
          }
          rows={8}
          className="rounded-xl border border-slate-200 px-3 py-2 font-mono text-xs"
        />
      </label>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <p className="font-medium text-slate-700">Supported placeholders</p>
        <p className="mt-1">{TEMPLATE_PLACEHOLDERS.join(", ")}</p>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={loading}
          className="rounded-xl bg-[#54B0AF] px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {selectedRule ? "Update Rule" : "Create Rule"}
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={onCancel}
          className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 disabled:opacity-50"
        >
          Clear
        </button>
      </div>
    </form>
  );
};

const Field = ({
  label,
  value,
  onChange,
  type = "text",
  min,
  error,
  helpText,
}) => (
  <label className="flex flex-col gap-1 text-sm">
    <span className="text-slate-500">{label}</span>
    <input
      type={type}
      value={value}
      min={min}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-xl border border-slate-200 px-3 py-2"
    />
    {helpText && <span className="text-xs text-slate-500">{helpText}</span>}
    {error && <span className="text-xs text-red-600">{error}</span>}
  </label>
);

const SelectField = ({ label, value, onChange, options, error, helpText }) => (
  <label className="flex flex-col gap-1 text-sm">
    <span className="text-slate-500">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-xl border border-slate-200 px-3 py-2"
    >
      {options.map((option) => (
        <option key={option.value || option} value={option.value || option}>
          {option.label || option}
        </option>
      ))}
    </select>
    {helpText && <span className="text-xs text-slate-500">{helpText}</span>}
    {error && <span className="text-xs text-red-600">{error}</span>}
  </label>
);

export default AlertRuleForm;

